import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

export type PopupPosition =
  | 'top left'
  | 'top right'
  | 'bottom right'
  | 'bottom left'
  | 'right center'
  | 'left center'
  | 'top center'
  | 'bottom center';

export interface PopupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * The position of the popup relative to the trigger
   */
  position?: PopupPosition;
  
  /**
   * Whether the popup should stay in a fixed position and allow hovering over content
   */
  hoverable?: boolean;
  
  /**
   * The trigger element that shows the popup on hover
   */
  trigger: React.ReactNode;
  
  /**
   * The popup content to display. Can be a React node or a function that receives closePopup as a parameter
   */
  children: React.ReactNode | ((closePopup: () => void) => React.ReactNode);
}

/**
 * Popup component that displays content on hover with smart positioning
 */
export const Popup = React.forwardRef<HTMLDivElement, PopupProps>(
  (
    {
      position = 'bottom center',
      hoverable = false,
      trigger,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    // Touch / coarse-pointer devices have no hover, and the cursor-follow
    // variant is positioned from mousemove — both are dead on a phone. When
    // this is true we switch the popup to tap-to-toggle, trigger-anchored
    // positioning, and pointer-events:auto so the content is reachable.
    const [isTouch, setIsTouch] = useState(false);
    const isClosingRef = useRef(false);
    const [isClosingForCSS, setIsClosingForCSS] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const mousePositionRef = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef<number>();
    const closeTimeoutRef = useRef<NodeJS.Timeout>();
    const exitAnimationTimeoutRef = useRef<NodeJS.Timeout>();
    const portalContainerRef = useRef<HTMLDivElement | null>(null);

    // Create portal container on mount
    useEffect(() => {
      const portalContainer = document.createElement('div');
      portalContainer.className = 'prometheus-popup-portal';
      portalContainer.style.position = 'absolute';
      portalContainer.style.top = '0';
      portalContainer.style.left = '0';
      portalContainer.style.pointerEvents = 'none';
      portalContainer.style.zIndex = '9999';
      
      document.body.appendChild(portalContainer);
      portalContainerRef.current = portalContainer;

      return () => {
        if (portalContainerRef.current && document.body.contains(portalContainerRef.current)) {
          document.body.removeChild(portalContainerRef.current);
        }
      };
    }, []);

    // Detect coarse/no-hover pointers (phones, touch tablets). Kept reactive so
    // a hybrid device that switches input mode updates too.
    useEffect(() => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
      const noHover = window.matchMedia('(hover: none)');
      const coarse = window.matchMedia('(pointer: coarse)');
      const compute = () => setIsTouch(noHover.matches || coarse.matches);
      compute();
      noHover.addEventListener?.('change', compute);
      coarse.addEventListener?.('change', compute);
      return () => {
        noHover.removeEventListener?.('change', compute);
        coarse.removeEventListener?.('change', compute);
      };
    }, []);

    const closePopup = useCallback(() => {
      if (!isVisible || isClosingRef.current) return;
      
      isClosingRef.current = true;
      setIsClosingForCSS(true);
      // Wait for exit animation to complete before removing from DOM
      exitAnimationTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        isClosingRef.current = false;
        setIsClosingForCSS(false);
      }, 150); // Slightly longer than CSS animation duration
    }, [isVisible]);

    const updatePopupPosition = useCallback(() => {
      if (!triggerRef.current || !popupRef.current || !isVisible) return;

      const triggerRect = triggerRef.current.children[0].getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = 0;
      let top = 0;

      // On touch there is no cursor to follow, so anchor to the trigger just
      // like the hoverable variant does.
      const anchored = hoverable || isTouch;

      if (anchored) {
        // Fixed position relative to trigger
        const [verticalPos, horizontalPos] = position.split(' ');
        
        // Calculate horizontal position based on alignment
        switch (horizontalPos) {
          case 'left':
            if (verticalPos === 'top' || verticalPos === 'bottom') {
              // "top left" or "bottom left" - align popup's left edge to button's left edge
              left = triggerRect.left;
            } else {
              // "left center" - popup to the left of trigger
              left = triggerRect.left - popupRect.width;
            }
            break;
          case 'right':
            if (verticalPos === 'top' || verticalPos === 'bottom') {
              // "top right" or "bottom right" - align popup's right edge to button's right edge
              left = triggerRect.right - popupRect.width;
            } else {
              // "right center" - popup to the right of trigger
              left = triggerRect.right;
            }
            break;
          case 'center':
            if (verticalPos === 'left') {
              // "left center" - popup to the left of trigger, vertically centered
              left = triggerRect.left - popupRect.width;
            } else if (verticalPos === 'right') {
              // "right center" - popup to the right of trigger, vertically centered
              left = triggerRect.right;
            } else {
              // "top center" or "bottom center" - center horizontally
              left = triggerRect.left + (triggerRect.width - popupRect.width) / 2;
            }
            break;
        }

        // Calculate vertical position
        switch (verticalPos) {
          case 'top':
            top = triggerRect.top - popupRect.height;
            break;
          case 'bottom':
            top = triggerRect.bottom;
            break;
          case 'right':
          case 'left':
            // For "right center" and "left center", vertically center the popup
            top = triggerRect.top + (triggerRect.height - popupRect.height) / 2;
            break;
        }
      } else {
        // Follow cursor position
        const [verticalPos, horizontalPos] = position.split(' ');
        const offset = 20; // Small offset from cursor
        const safetyPadding = 20;

        if (
          !isClosingRef.current && (
            mousePositionRef.current.x + safetyPadding < triggerRect.left ||
            mousePositionRef.current.x - safetyPadding > triggerRect.right ||
            mousePositionRef.current.y + safetyPadding < triggerRect.top ||
            mousePositionRef.current.y - safetyPadding > triggerRect.bottom
          )
        ) {
          closePopup()
        }
        
        // Calculate horizontal position based on alignment
        switch (horizontalPos) {
          case 'left':
            if (verticalPos === 'top' || verticalPos === 'bottom') {
              // "top left" or "bottom left" - align popup's left edge to cursor position
              left = mousePositionRef.current.x;
            } else {
              // "left center" - popup to the left of cursor
              left = mousePositionRef.current.x - popupRect.width - offset;
            }
            break;
          case 'right':
            if (verticalPos === 'top' || verticalPos === 'bottom') {
              // "top right" or "bottom right" - align popup's right edge to cursor position
              left = mousePositionRef.current.x - popupRect.width;
            } else {
              // "right center" - popup to the right of cursor
              left = mousePositionRef.current.x + offset;
            }
            break;
          case 'center':
            if (verticalPos === 'left') {
              // "left center" - popup to the left of cursor, vertically centered
              left = mousePositionRef.current.x - popupRect.width - offset;
            } else if (verticalPos === 'right') {
              // "right center" - popup to the right of cursor, vertically centered
              left = mousePositionRef.current.x + offset;
            } else {
              // "top center" or "bottom center" - center horizontally
              left = mousePositionRef.current.x - popupRect.width / 2;
            }
            break;
        }

        // Calculate vertical position
        switch (verticalPos) {
          case 'top':
            top = mousePositionRef.current.y - popupRect.height - offset;
            break;
          case 'bottom':
            top = mousePositionRef.current.y + offset;
            break;
          case 'right':
          case 'left':
            // For "right center" and "left center", vertically center relative to cursor
            top = mousePositionRef.current.y - popupRect.height / 2;
            break;
        }
      }

      // Smart positioning - prevent bleeding off screen
      if (left < 0) {
        left = 8; // Small margin from edge
      } else if (left + popupRect.width > viewportWidth) {
        left = viewportWidth - popupRect.width - 8;
      }

      if (top < 0) {
        top = 8; // Small margin from edge
      } else if (top + popupRect.height > viewportHeight) {
        top = viewportHeight - popupRect.height - 8;
      }

      popupRef.current.style.left = `${left}px`
      popupRef.current.style.top = `${top}px`
      popupRef.current.style.position = 'fixed'
      popupRef.current.style.zIndex = '9999'
    }, [position, hoverable, isTouch, isVisible, closePopup]);

    const handleMouseEnter = useCallback((event: MouseEvent) => {
      mousePositionRef.current = { x: event.clientX, y: event.clientY }
      // Cancel any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = undefined;
      }
      // Cancel any pending exit animation
      if (exitAnimationTimeoutRef.current) {
        clearTimeout(exitAnimationTimeoutRef.current);
        exitAnimationTimeoutRef.current = undefined;
      }
      isClosingRef.current = false;
      setIsClosingForCSS(false);
      setIsVisible(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      if (!hoverable) {
        closePopup();
      } else {
        // For hoverable popups, delay closing to allow user to move to popup content
        closeTimeoutRef.current = setTimeout(() => {
          closePopup();
        }, 100);
      }
    }, [hoverable, closePopup]);

    const handlePopupMouseEnter = useCallback(() => {
      // Cancel the close timeout when entering popup content
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = undefined;
      }
      // Cancel any pending exit animation
      if (exitAnimationTimeoutRef.current) {
        clearTimeout(exitAnimationTimeoutRef.current);
        exitAnimationTimeoutRef.current = undefined;
      }
      isClosingRef.current = false;
      setIsClosingForCSS(false);
    }, []);

    const handlePopupMouseLeave = useCallback(() => {
      if (hoverable) {
        closePopup();
      }
    }, [hoverable, closePopup]);

    // Tap-to-toggle for touch devices (no hover). Opening cancels any pending
    // close/exit animation so a double tap doesn't get stuck mid-transition.
    const handleTriggerClick = useCallback(() => {
      if (isVisible) {
        closePopup();
        return;
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = undefined;
      }
      if (exitAnimationTimeoutRef.current) {
        clearTimeout(exitAnimationTimeoutRef.current);
        exitAnimationTimeoutRef.current = undefined;
      }
      isClosingRef.current = false;
      setIsClosingForCSS(false);
      setIsVisible(true);
    }, [isVisible, closePopup]);

    const handleMouseMove = useCallback((event: MouseEvent) => {
      if (!hoverable && !isTouch && isVisible) {
        mousePositionRef.current = { x: event.clientX, y: event.clientY };
        
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        
        animationFrameRef.current = requestAnimationFrame(updatePopupPosition);
      }
    }, [hoverable, isVisible, updatePopupPosition]);

    const handleScroll = useCallback(() => {
      // Close popup when any scrolling occurs to prevent misalignment
      if (isVisible) {
        updatePopupPosition();
      }
    }, [isVisible, updatePopupPosition]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    }, [closePopup]);

    useLayoutEffect(() => {
      if (isVisible) {
        updatePopupPosition();

        if (!hoverable && !isTouch) {
          document.addEventListener('mousemove', handleMouseMove);
        }
        
        // Listen for scroll events on window and all scrollable containers
        // Using capture phase to catch all scroll events
        document.addEventListener('scroll', handleScroll, true);
        window.addEventListener('scroll', handleScroll);
        
        // Listen for Escape key to close popup
        document.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
        // Don't clear exitAnimationTimeoutRef here - let it complete naturally
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [isVisible, hoverable, isTouch, handleMouseMove, updatePopupPosition, handleScroll, handleKeyDown]);

    useLayoutEffect(() => {
      if (isVisible) {
        updatePopupPosition()
      }
    }, [isVisible, updatePopupPosition]);

    // Cleanup effect for component unmount
    useEffect(() => {
      return () => {
        // Only clear exitAnimationTimeoutRef on component unmount, not on state changes
        if (exitAnimationTimeoutRef.current) {
          clearTimeout(exitAnimationTimeoutRef.current);
        }
      };
    }, []);

    // On touch, close when the user taps outside both the trigger and the
    // popup content. The tap that opened the popup has already finished by the
    // time this listener is attached, so it won't self-close.
    useEffect(() => {
      if (!isVisible || !isTouch) return;
      const onPointerDown = (event: Event) => {
        const target = event.target as Node | null;
        if (!target) return;
        if (triggerRef.current?.contains(target)) return;
        if (popupRef.current?.contains(target)) return;
        closePopup();
      };
      document.addEventListener('pointerdown', onPointerDown, true);
      return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [isVisible, isTouch, closePopup]);

    return (
      <div
        ref={ref}
        className={clsx('prometheus-popup', className)}
        {...props}
      >
        <div
          ref={triggerRef}
          className="prometheus-popup__trigger"
          onMouseEnter={isTouch ? undefined : (e) => handleMouseEnter(e.nativeEvent)}
          onMouseLeave={isTouch ? undefined : handleMouseLeave}
          onClick={isTouch ? handleTriggerClick : undefined}
        >
          {trigger}
        </div>

        {isVisible && isTouch && portalContainerRef.current && createPortal(
          // Touch: a dim scrim gives a large, obvious tap target to dismiss the
          // popup (its content can fill most of a phone screen) and blocks
          // stray taps on the UI behind it.
          <div
            className="prometheus-popup__scrim"
            onClick={closePopup}
          />,
          portalContainerRef.current
        )}

        {isVisible && portalContainerRef.current && createPortal(
          <div
            ref={popupRef}
            className={clsx(
              'prometheus-popup__content',
              `prometheus-popup__content--${position.replace(' ', '-')}`,
              {
                // On touch we anchor to the trigger (like hoverable) so the
                // content is interactive (pointer-events:auto) rather than the
                // cursor-follow variant which is pointer-events:none.
                'prometheus-popup__content--hoverable': hoverable || isTouch,
                'prometheus-popup__content--cursor-follow': !hoverable && !isTouch,
                'prometheus-popup__content--touch': isTouch,
                'prometheus-popup__content--closing': isClosingForCSS,
              }
            )}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            {typeof children === 'function' ? children(closePopup) : children}
          </div>,
          portalContainerRef.current
        )}
      </div>
    );
  }
);

Popup.displayName = 'Popup';

export default Popup;